import {
  Button,
  DesignLanguage,
  Grid,
  Input,
  InputEmailPhone,
  Portal,
  Text,
  ToastItem,
  useIsBreakpointLargeAndUp,
  useIsDesignLanguage,
  useToast,
} from '@afterpaytouch/core'
import { DDPrivacyLevel } from '@afterpaytouch/integrations/datadog'
import { MaskedElement } from '@afterpaytouch/integrations/MaskedElement'
import { ErrorCode } from '@afterpaytouch/portal-api/types'
import { isPortalApiError } from '@afterpaytouch/portal-api/utils'
import { unwrapResult } from '@reduxjs/toolkit'
import React, { ChangeEvent, createRef, FormEvent, FunctionComponent, ReactNode, useEffect, useState } from 'react'

import { ACCEPTABLE_PASSWORD_STRENGTH } from '../../model'
import { authReset, useAppDispatch, useConsumerSelector, useMobileLogin, useResetPassword } from '../../state'
import { useTranslation } from '../../utils/convergence'
import { useDockedFooterElement } from '../../utils/dockedFooter'
import { useCheckoutSizes } from '../../utils/responsive'
import { Form } from '../Form'
import { getUsername } from '../LoginPassword/utils'
import { ProfileCard } from '../ProfileCard'
import { useProfileCardProps } from './hooks'

interface Props {
  onSuccess: () => void
  onError: () => void
}

const errorKeys: Partial<Record<ErrorCode, string>> = {
  [ErrorCode.WeakPassword]: 'weakPassword',
  [ErrorCode.IdenticalPassword]: 'identicalPassword',
  [ErrorCode.InvalidNewPassword]: 'weakPassword',
  [ErrorCode.RecentPassword]: 'identicalPassword',
}

const fatalErrors: Set<ErrorCode> = new Set([ErrorCode.PasswordResetNotAllowed])

const ToastMessage: FunctionComponent<{ headline: string; body: string }> = ({ headline, body }) => {
  return (
    <>
      <ToastItem.Headline>{headline}</ToastItem.Headline>
      <ToastItem.Body>{body}</ToastItem.Body>
    </>
  )
}

export const CreatePassword: FunctionComponent<Props> = ({ onSuccess, onError }) => {
  const { t } = useTranslation()
  const dispatch = useAppDispatch()
  const { code } = useResetPassword()
  const [formData, setFormData] = useState({
    password: '',
    'remember-me': false,
  })

  const [errorMessage, setErrorMessage] = useState('')
  const [passwordStrength, setPasswordStrength] = useState(0)
  const [locked, setLocked] = useState(false)
  const inputRef = createRef<HTMLInputElement>()
  const { textSize, buttonSize } = useCheckoutSizes()
  const { hasPassword, email, mobile } = useConsumerSelector()
  const { isMobileLogin } = useMobileLogin()
  const username = getUsername(isMobileLogin, mobile, email)
  const isArcadeDesignLanguage = useIsDesignLanguage(DesignLanguage.Arcade)
  const isBronteDesignLanguage = useIsDesignLanguage(DesignLanguage.Bronte)
  const profileCardProps = useProfileCardProps()
  const setToast = useToast()

  const setError = (errorKey?: string): void => {
    if (errorKey === undefined) {
      setErrorMessage(t('common:error:unknown'))
      return
    }

    if (errorKey === 'weakPassword') {
      setErrorMessage(t('createPassword:error:weakPassword'))
      return
    }

    if (errorKey === 'identicalPassword') {
      setErrorMessage(t('createPassword:error:identicalPassword'))
      return
    }

    if (errorKey === 'emptyPassword') {
      setErrorMessage(t('createPassword:error:emptyPassword'))
      return
    }

    setErrorMessage(t('common:error:unknown'))
  }

  const clearError = (): void => setErrorMessage('')
  const lock = (): void => setLocked(true)
  const unlock = (): void => setLocked(false)
  const isError = errorMessage !== ''
  const placeholder = isArcadeDesignLanguage ? t('createPassword:inputPlaceholder') : undefined
  const disabled = locked || (isArcadeDesignLanguage && formData.password === '')

  const { footerElement } = useDockedFooterElement()
  const isBreakpointLargeAndUp = useIsBreakpointLargeAndUp()
  const shouldUsePortal = footerElement !== null && isBreakpointLargeAndUp === false

  useEffect(() => {
    if (passwordStrength >= ACCEPTABLE_PASSWORD_STRENGTH) {
      clearError()
    }
  }, [passwordStrength])

  const onFormChange = (ev: ChangeEvent<HTMLInputElement>): void => {
    setFormData({
      ...formData,
      [ev.target.name]: ev.target.value,
    })
  }

  const handleSubmit = async (ev: FormEvent): Promise<void> => {
    ev.preventDefault()
    if (formData.password === '') {
      return setError('emptyPassword')
    }
    if (passwordStrength < ACCEPTABLE_PASSWORD_STRENGTH) {
      return setError('weakPassword')
    }
    clearError()
    lock()
    try {
      unwrapResult(await dispatch(authReset({ code, ...formData })))
      onSuccess()
      if (isArcadeDesignLanguage) {
        setToast({ message: <ToastMessage headline={t('createPassword:toastHeadline')} body={t('createPassword:toastBody')} />, kind: 'Success' })
      }
    } catch (e) {
      if (isPortalApiError(e) && fatalErrors.has(e.errorCode)) {
        onError()
        return
      }
      setError(isPortalApiError(e) ? errorKeys[e.errorCode] : undefined)
      unlock()
    }
  }

  useEffect(() => {
    inputRef.current?.focus()
    inputRef.current?.scrollIntoView({ behavior: 'smooth' })
  }, [])

  const renderCTA = (): ReactNode => (
    <Button.Primary
      size={buttonSize}
      type={shouldUsePortal ? 'button' : 'submit'}
      onClick={shouldUsePortal ? handleSubmit : undefined}
      testNameSpace='create-password'
      disabled={disabled}
      padding='Fluid'
    >
      {t('common:continue')}
    </Button.Primary>
  )

  const descriptionText = hasPassword ? t('createPassword:description') : t('createPassword:description', { context: 'firstPassword' })

  const passwordStrengthLabel = [
    t('createPassword:passwordStrength', { context: 'weak' }),
    t('createPassword:passwordStrength', { context: 'good' }),
    t('createPassword:passwordStrength', { context: 'great' }),
  ]

  return (
    <Form onSubmit={handleSubmit}>
      {isBronteDesignLanguage && <ProfileCard {...profileCardProps} />}
      <Grid>
        <Text size={textSize} id='createPasswordLabel' color={isArcadeDesignLanguage ? 'Subtle' : 'Black'}>
          {descriptionText}
        </Text>
      </Grid>
      <Grid>
        <InputEmailPhone type='hidden' value={username} autoComplete='username' name='username' testNameSpace='create-password-username-hidden' />
        <MaskedElement stylingModifier='wrap' privacyLevel={DDPrivacyLevel.Hidden} actionName='Create Password Input'>
          <Input.Password
            ref={inputRef}
            onChange={onFormChange}
            label={t('createPassword:inputLabel')}
            placeholder={placeholder}
            testNameSpace='create-password'
            name='password'
            autoComplete='new-password'
            aria-labelledby='createPasswordLabel'
            defaultPasswordMasked={false}
            passwordStrengthLabel={passwordStrengthLabel}
            onPasswordStrengthChange={setPasswordStrength}
            disabled={locked}
            error={isError}
            errorText={errorMessage}
          />
        </MaskedElement>
      </Grid>
      <Grid>
        <Grid.Item>{shouldUsePortal ? <Portal container={footerElement ?? undefined}>{renderCTA()}</Portal> : renderCTA()}</Grid.Item>
      </Grid>
    </Form>
  )
}
